import i18n from '@/lib/i18n';
import type { AnyWidget } from './_sdk/types';

/**
 * Widget registry, PRD VS-040 / TechSpec §9.1.1: every `src/widgets/<type>/index.ts`
 * registers itself at build time — adding a widget touches no existing file.
 * Its strings come from `<type>/locales/fr.json`, namespace `widget.<type>`.
 */

type ManifestModules = Record<string, { default?: unknown }>;
type LocaleModules = Record<string, { default: Record<string, unknown> }>;

const folderOf = (path: string) => /^\.\/([^/]+)\//.exec(path)?.[1] ?? '';

const REQUIRED = ['type', 'category', 'defaultSize', 'minSize', 'propsSchema', 'useData'] as const;

function isWidget(value: unknown): value is AnyWidget {
  return (
    typeof value === 'object' &&
    value !== null &&
    REQUIRED.every((k) => k in value) &&
    'Render' in value &&
    'Editor' in value
  );
}

/** Pure, for tests: a broken module is reported and skipped, never fatal. */
export function createWidgetRegistry(
  manifests: ManifestModules,
  locales: LocaleModules,
  // biome-ignore lint/suspicious/noConsole: a broken widget folder must be visible in the browser console, the only log the web app has
  report: (message: string) => void = (m) => console.error(m),
): ReadonlyMap<string, AnyWidget> {
  const registry = new Map<string, AnyWidget>();
  for (const [path, mod] of Object.entries(manifests)) {
    const folder = folderOf(path);
    if (folder.startsWith('_')) continue;
    const widget = mod.default;
    if (!isWidget(widget)) {
      report(`widget ${folder}: index.ts does not default-export defineWidget({...})`);
      continue;
    }
    if (widget.type !== folder) {
      report(`widget ${folder}: type "${widget.type}" must equal its folder name`);
      continue;
    }
    registry.set(widget.type, widget);
  }
  for (const [path, mod] of Object.entries(locales)) {
    const folder = folderOf(path);
    if (registry.has(folder)) i18n.addResourceBundle('fr', `widget.${folder}`, mod.default, true);
  }
  return registry;
}

export const widgetRegistry = createWidgetRegistry(
  import.meta.glob('./*/index.ts', { eager: true }),
  import.meta.glob('./*/locales/fr.json', { eager: true }),
);

/** The catalog order: by category, then by name. */
export function listWidgets(registry = widgetRegistry): AnyWidget[] {
  return [...registry.values()].sort(
    (a, b) =>
      a.category.localeCompare(b.category) || widgetName(a.type).localeCompare(widgetName(b.type)),
  );
}

export function widgetName(type: string): string {
  // Runtime namespace: see widgetText().
  const t = i18n.t as unknown as (key: string, options?: Record<string, unknown>) => string;
  return t('name', { ns: `widget.${type}`, defaultValue: type });
}
